import React from 'react';
import styled from 'styled-components';

import MapWithMarker from '../common/MapWithMarker';
import {
  createRentalLocation,
  getRentalLocations,
  updateRentalLocation,
} from '../../api/rental';
import Layout from './Layout';
import {
  ActionButton,
  Actions,
  Badge,
  Card,
  CardTitle,
  ErrorText,
  Field,
  FieldGrid,
  Grid,
  Input,
  List,
  ListButton,
  State,
  SuccessText,
  Textarea,
} from './styles';

const blankLocation = {
  title: '',
  city: '',
  address: '',
  coordinates: {lat: 44.952117, lng: 34.102417},
  opens_at: '09:00',
  closes_at: '20:00',
  phone: '',
  comment: '',
  hidden: false,
};

const toForm = location => location ? ({
  ...blankLocation,
  ...location,
  phone: location.phone || '',
  comment: location.comment || '',
  coordinates: location.coordinates || blankLocation.coordinates,
}) : {...blankLocation};

const toPayload = form => ({
  title: form.title.trim(),
  city: form.city.trim(),
  address: form.address.trim(),
  coordinates: form.coordinates,
  opens_at: form.opens_at,
  closes_at: form.closes_at,
  phone: form.phone.trim() || null,
  comment: form.comment.trim() || null,
  hidden: Boolean(form.hidden),
});

function LocationsContent({authorization}) {
  const [locations, setLocations] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState(null);
  const [form, setForm] = React.useState({...blankLocation});
  const [state, setState] = React.useState('loading');
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  const load = React.useCallback(async () => {
    if (!authorization) { return; }
    setState('loading');
    try {
      const list = await getRentalLocations(authorization);
      setLocations(list);
      setState('ready');
    } catch (requestError) {
      setError(requestError.message);
      setState('ready');
    }
  }, [authorization]);

  React.useEffect(() => { load(); }, [load]);

  const select = location => {
    setSelectedId(location ? location.id : null);
    setForm(toForm(location));
    setError('');
    setSuccess('');
  };

  const change = (field, value) => setForm(current => ({...current, [field]: value}));

  const submit = async () => {
    setError('');
    setSuccess('');
    if (!form.title.trim() || !form.city.trim() || !form.address.trim()) {
      setError('Название, город и адрес обязательны');
      return;
    }
    setState('saving');
    try {
      const payload = toPayload(form);
      if (selectedId) {
        await updateRentalLocation(authorization, selectedId, payload);
        setSuccess('Пункт выдачи сохранён');
      } else {
        const created = await createRentalLocation(authorization, payload);
        setSelectedId(created.id);
        setSuccess('Пункт выдачи создан');
      }
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setState('ready');
    }
  };

  return (
    <Grid>
        <Card>
          <CardTitle>Пункты выдачи</CardTitle>
          {state === 'loading' && <State>Загрузка…</State>}
          {state !== 'loading' && locations.length === 0 && (
            <State>Пунктов пока нет. Создайте первый — без него автомобиль негде выдавать.</State>
          )}
          <List>
            {locations.map(location => (
              <ListButton
                key={location.id}
                active={location.id === selectedId}
                onClick={() => select(location)}
              >
                <div>
                  <strong>{location.city}</strong> · {location.title}
                  <div>{location.address}</div>
                </div>
                <Badge>
                  {location.vehicles_available} из {location.vehicles_total}
                </Badge>
              </ListButton>
            ))}
          </List>
          <Actions>
            <ActionButton onClick={() => select(null)}>Добавить пункт</ActionButton>
          </Actions>
        </Card>

        <Card>
          <CardTitle>{selectedId ? 'Редактирование пункта' : 'Новый пункт'}</CardTitle>
          <FieldGrid>
            <Field>
              <span>Город</span>
              <Input value={form.city} onChange={event => change('city', event.target.value)} />
            </Field>
            <Field>
              <span>Название</span>
              <Input value={form.title} onChange={event => change('title', event.target.value)} />
            </Field>
            <Field>
              <span>Адрес</span>
              <Input value={form.address} onChange={event => change('address', event.target.value)} />
            </Field>
            <Field>
              <span>Телефон оператора</span>
              <Input value={form.phone} onChange={event => change('phone', event.target.value)} />
            </Field>
            <Field>
              <span>Открытие</span>
              <Input type="time" value={form.opens_at} onChange={event => change('opens_at', event.target.value)} />
            </Field>
            <Field>
              <span>Закрытие</span>
              <Input type="time" value={form.closes_at} onChange={event => change('closes_at', event.target.value)} />
            </Field>
          </FieldGrid>

          <Field>
            <span>Комментарий для оператора</span>
            <Textarea
              rows={3}
              value={form.comment}
              onChange={event => change('comment', event.target.value)}
            />
          </Field>

          <Field>
            <span>Координаты пункта</span>
            <MapBox>
              <MapWithMarker
                center={{lat: Number(form.coordinates.lat), lng: Number(form.coordinates.lng)}}
                handleMarkerChange={map => change('coordinates', {lat: map.center[0], lng: map.center[1]})}
              />
            </MapBox>
          </Field>

          <Field>
            <label>
              <input
                type="checkbox"
                checked={form.hidden}
                onChange={event => change('hidden', event.target.checked)}
              />
              {' '}Скрыть пункт с клиентской карты
            </label>
          </Field>

          {error && <ErrorText>{error}</ErrorText>}
          {success && <SuccessText>{success}</SuccessText>}

          <Actions>
            <ActionButton primary disabled={state === 'saving'} onClick={submit}>
              {state === 'saving' ? 'Сохранение…' : 'Сохранить'}
            </ActionButton>
            {selectedId && (
              <ActionButton onClick={() => select(null)}>Отменить</ActionButton>
            )}
          </Actions>
        </Card>
    </Grid>
  );
}

/**
 * Layout передаёт authorization и role дочернему элементу через cloneElement,
 * поэтому содержимое вынесено в отдельный компонент.
 */
function Locations() {
  return (
    <Layout
      title="Локации проката"
      description="Пункты выдачи и возврата автомобилей. Автомобиль привязывается к пункту, возврат выполняется в тот же пункт."
    >
      <LocationsContent />
    </Layout>
  );
}

const MapBox = styled.div`
  height: 320px;
  margin-top: 10px;
  border: 1px solid #cad0d9;
  border-radius: 8px;
  overflow: hidden;
`;

export default Locations;
